import { useReducedMotion } from "framer-motion";
import { useState } from "react";
import { CAMERA_DURATION } from "../camera/motion";
import { usePresenter } from "./context";
import { Footnotes } from "./Footnotes";
import type { ScreenshotBlock } from "./model";

const VIEW_W = 758; // inside the 760px frame's borders
const VIEW_H = 470;

type Rect = { left: number; top: number; width: number; height: number };

function containRect(natural: { w: number; h: number } | null): Rect {
  if (!natural) return { left: 0, top: 0, width: VIEW_W, height: VIEW_H };
  const s = Math.min(VIEW_W / natural.w, VIEW_H / natural.h);
  const width = natural.w * s;
  const height = natural.h * s;
  return { left: (VIEW_W - width) / 2, top: (VIEW_H - height) / 2, width, height };
}

export function ScreenshotSlide({
  block,
  slideIndex,
  stop,
}: {
  block: ScreenshotBlock;
  slideIndex: number;
  stop: number;
}) {
  const { goto } = usePresenter();
  const reduced = useReducedMotion() ?? false;
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const box = containRect(block.assetId ? natural : null);
  const active = stop > 0 ? block.pins[stop - 1] : undefined;
  const pick = (n: number) => goto({ slideIndex, stop: stop === n ? 0 : n });

  // The camera: scale the whole image layer (pins included) around the active pin.
  const origin = active ? `${box.left + active.x * box.width}px ${box.top + active.y * box.height}px` : "50% 50%";
  const zoom = active ? active.zoom : 1;
  const layerStyle = {
    transformOrigin: origin,
    transform: reduced ? undefined : `scale(${zoom})`,
    transition: reduced ? undefined : `transform ${CAMERA_DURATION}s var(--ease-camera)`,
  };

  return (
    <div className="slide">
      <h2 className="abs shot-title" style={{ margin: 0 }}>
        {block.title}
      </h2>
      <div className="abs shot-frame">
        <div className="shot-bar">
          <span>{block.filename}</span>
          <span data-testid="zoom-label">{active ? `ZOOM ${Math.round(active.zoom * 100)}%` : "FIT"}</span>
        </div>
        <div className="shot-view">
          {!block.assetId && (
            // Outside the zoom layer: stays readable while the pins zoom over the empty frame.
            <div className="shot-placeholder">
              <span className="shot-placeholder-name">{block.filename}</span>
              <span>[Placeholder: your uploaded screenshot goes here]</span>
            </div>
          )}
          <div className="shot-layer" style={layerStyle}>
            {block.assetId && (
              <img
                className="shot-img"
                src={`/api/assets/${encodeURIComponent(block.assetId)}`}
                alt={block.caption ?? block.title}
                style={box}
                onLoad={(e) => setNatural({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              />
            )}
            <div className="abs" style={box}>
              {block.pins.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  className="pin"
                  style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%` }}
                  aria-pressed={stop === i + 1}
                  aria-label={`Question ${i + 1}: ${p.question}`}
                  onClick={() => pick(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="abs shot-side">
        <span className="eyebrow">Talk it through</span>
        {block.pins.map((p, i) => (
          <button
            key={p.id}
            type="button"
            className="question"
            aria-pressed={stop === i + 1}
            onClick={() => pick(i + 1)}
          >
            <span className="question-num">{i + 1}</span>
            <span>{p.question}</span>
          </button>
        ))}
        {block.pins.length > 0 && (
          <button type="button" className="link-btn" onClick={() => goto({ slideIndex, stop: 0 })}>
            Show the whole diagram
          </button>
        )}
        {block.caption && <p className="shot-caption">{block.caption}</p>}
      </div>
      <Footnotes refs={block.refs} />
    </div>
  );
}
